import {
  ChangeEvent,
  DragEvent,
  useCallback,
  useRef,
  useState,
} from "react";

type UsePictureUploadOptions = {
  onFilesSelected: (files: File[]) => void;
  multiple?: boolean;
};

export const usePictureUpload = ({
  onFilesSelected,
  multiple = true,
}: UsePictureUploadOptions) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  const processFiles = useCallback(
    (files: FileList | File[]) => {
      const selectedFiles = Array.from(files);

      if (selectedFiles.length > 0) {
        onFilesSelected(selectedFiles);
      }
    },
    [onFilesSelected]
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        processFiles(event.target.files);
        event.target.value = "";
      }
    },
    [processFiles]
  );

  const handleDragEnter = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current += 1;
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragCounterRef.current -= 1;

    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  const handleDragOver = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragActive(false);

      if (event.dataTransfer.files) {
        processFiles(event.dataTransfer.files);
      }
    },
    [processFiles]
  );

  return {
    fileInputRef,
    isDragActive,
    openFilePicker,
    inputProps: {
      type: "file" as const,
      multiple,
      onChange: handleFileInputChange,
    },
    dragDropProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
};
